/**
 * Central GraphQL auth-mode switch.
 *
 * The API's default auth is AWS_IAM: guests read the public catalog through the
 * Cognito identity pool's unauth role. Anything behind an owner or group rule
 * (orders, cart, admin screens) needs the user-pool token instead.
 *
 * `defaultAuthMode` is resolved per request by @aws-amplify/api-graphql, so
 * re-configuring here flips the mode for every existing client at once. That
 * saves threading `authMode` through ~75 generateClient() call sites.
 */
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession } from 'aws-amplify/auth';

const setMode = (mode) => {
  Amplify.configure(Amplify.getConfig(), {
    API: { GraphQL: { defaultAuthMode: mode } },
  });
};

export const setUserPoolAuthMode = () => setMode('userPool');
export const setGuestAuthMode = () => setMode('iam');

/**
 * Is there a Cognito session on disk? Synchronous on purpose.
 *
 * fetchAuthSession is async, so between first paint and its resolution every
 * query went out as `iam`. For a signed-in admin that means staff mutations are
 * refused -- deleting a product failed with "Not Authorized to access
 * deleteProduct" while the same call with the token succeeded. Reading the
 * token's presence straight from storage lets the mode be right on the very
 * first render, with the async check still confirming it after.
 */
const hasStoredSession = () => {
  try {
    return Object.keys(window.localStorage).some(
      (k) => k.startsWith('CognitoIdentityServiceProvider.') && k.endsWith('.idToken'),
    );
  } catch {
    return false;
  }
};

/** Set the best mode we can know synchronously, before anything renders. */
export const primeGraphqlAuthMode = () => {
  if (hasStoredSession()) setUserPoolAuthMode();
  else setGuestAuthMode();
};

/** Pick the mode for the session we already have, then follow auth events. */
export const initGraphqlAuthMode = async () => {
  primeGraphqlAuthMode();
  try {
    const session = await fetchAuthSession();
    if (session?.tokens?.idToken) {
      setUserPoolAuthMode();
    } else {
      setGuestAuthMode();
    }
  } catch {
    setGuestAuthMode();
  }

  Hub.listen('auth', ({ payload }) => {
    switch (payload.event) {
      case 'signedIn':
      case 'tokenRefresh':
        setUserPoolAuthMode();
        break;
      case 'signedOut':
      case 'tokenRefresh_failure':
        setGuestAuthMode();
        break;
      default:
        break;
    }
  });
};
